import type {GroupField, KeyTextField, RichTextField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import type {EventDocumentDataSpeakersItem, Simplify} from 'prismicio-types'
import {useCallback, useState} from 'react'
import {Container} from 'src/common/Container'
import {SpeakerCard} from './components/SpeakerCard'

type Props = {
  aboutTitle: KeyTextField
  aboutDescription: RichTextField
  speakersTitle: KeyTextField
  speakers: GroupField<Simplify<EventDocumentDataSpeakersItem>>
}

export const AboutSection = ({aboutTitle, aboutDescription, speakersTitle, speakers}: Props) => {
  const [expandedBioIndex, setExpandedBioIndex] = useState<number | null>(null)

  const toggleBio = useCallback((index: number) => {
    setExpandedBioIndex((prev) => (prev === index ? null : index))
  }, [])

  const hasAbout = isFilled.richText(aboutDescription)
  const hasSpeakers = isFilled.group(speakers)

  if (!hasAbout && !hasSpeakers) {
    return null
  }

  return (
    <Container
      className="relative mx-auto scroll-mt-16 px-5 !pb-0 !pt-10 sm:!pt-8 md:px-10 lg:px-5 xl:px-0"
      backgroundColor="Grey/100"
    >
      <div className="flex w-full min-w-0 max-w-[1200px] flex-col border-b border-b-moss-grey-100">
        <div className="flex flex-col gap-8 rounded-lg border border-black/10 bg-moss-white p-6 md:gap-12 md:p-8 lg:flex-row lg:gap-16">
          {hasAbout && (
            <div className="flex-1">
              {isFilled.keyText(aboutTitle) && (
                <h2 className="mb-4 text-24 font-medium leading-8 text-2a2928 md:leading-10">{aboutTitle}</h2>
              )}
              <PrismicRichText
                components={{
                  heading4: ({children}) => (
                    <h4 className="mt-6 text-12 font-semibold uppercase text-moss-black md:mt-8">{children}</h4>
                  ),
                  paragraph: ({children}) => <p className="text-tg-body-l mt-4 text-moss-grey-400">{children}</p>,
                  strong: ({children}) => <strong className="mt-4 font-normal text-moss-black">{children}</strong>,
                }}
                field={aboutDescription}
              />
            </div>
          )}

          {hasSpeakers && (
            <div className="flex-1">
              {isFilled.keyText(speakersTitle) && (
                <h2 className="mb-8 text-24 font-medium leading-8 text-2a2928 md:leading-10">{speakersTitle}</h2>
              )}
              <div className={hasAbout ? 'space-y-6' : 'grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-x-12 md:gap-y-6'}>
                {speakers.map((speaker, index) => {
                  const isLast = index === speakers.length - 1
                  const inGridMode = !hasAbout

                  return (
                    <SpeakerCard
                      key={index}
                      speaker={speaker}
                      isExpanded={expandedBioIndex === index}
                      onToggle={() => toggleBio(index)}
                      isLast={isLast}
                      inGridMode={inGridMode}
                    />
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </Container>
  )
}
